import { Button } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Webcam from 'react-webcam'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
    AziendaObj,
    selectAzienda,
    selectTipiImageDocColliConfez,
    selectTipiImageDocColliConfezRighe,
    TipiImageDoc,
} from '../impostazioni/impostazioniSlice'
import { ImmaginiProps } from './Immagini'
import { uploadImage } from './immaginiAPI'
import { addImage, ImmagineRequest, selectCameraImmaginiDefault, TIPO_IMAGE } from './immaginiSlice'
import { NO_CAMERA } from './ImmaginiVideoSelector'

declare var ImageCapture: any

// 1. DEFINIZIONE DEI TIPI
// Importiamo il tipo 'Webcam' per usarlo con useRef
type ResolutionOption = {
    label: string
    width: number
    height: number
}

type CaptureMode = 'image-capture' | 'fallback' | null

// Funzione helper per calcolare l'aspect ratio
const getAspectRatio = (width: number, height: number): string => {
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
    const commonDivisor = gcd(width, height)
    return `${width / commonDivisor}:${height / commonDivisor}`
}

export function ImmaginiCamera({ TIPO, id, DOC_ID }: ImmaginiProps) {
    // 3. TIPizzazione del REF
    const webcamRef = useRef<Webcam>(null)

    // 4. TIPizzazione degli STATI (useState)
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
    const [cameraCapabilities, setCameraCapabilities] = useState<MediaTrackCapabilities | null>(null)
    const [resolutionOptions, setResolutionOptions] = useState<ResolutionOption[]>([])
    const [selectedResolution, setSelectedResolution] = useState<ResolutionOption | null>(null)
    const [captureMode, setCaptureMode] = useState<CaptureMode>(null)

    const [videoConstraints, setVideoConstraints] = useState<any>()

    const cameraDefault = useAppSelector(selectCameraImmaginiDefault)

    // 5. TIPizzazione dei PARAMETRI delle CALLBACK
    const handleDevices = useCallback(
        (mediaDevices: MediaDeviceInfo[]) => {
            const videoDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput')
            setDevices(videoDevices)
        },
        [cameraDefault.deviceId]
    )

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(handleDevices)
    }, [handleDevices])

    const onUserMedia = (stream: MediaStream) => {
        if (webcamRef.current && webcamRef.current.stream) {
            const track = webcamRef.current.stream.getVideoTracks()[0]
            const capabilities = track.getCapabilities()

            console.log('Capacità della fotocamera:', capabilities)
            setCameraCapabilities(capabilities)

            if (capabilities.width?.max && capabilities.height?.max) {
                const maxWidth = capabilities.width.max
                const maxHeight = capabilities.height.max
                const aspectRatio = maxWidth / maxHeight

                const maxRes: ResolutionOption = {
                    label: `Massima (${maxWidth}x${maxHeight})`,
                    width: maxWidth,
                    height: maxHeight,
                }

                const options: ResolutionOption[] = [maxRes]

                setResolutionOptions(options)
                setSelectedResolution(maxRes)

                // L'oggetto videoConstraints viene inferito correttamente da TypeScript
                const videoConstraints = {
                    deviceId: cameraDefault.deviceId,
                    // width: 1000,//maxWidth,
                    // height: 1000 
                    // maxHeight,
                }

                setVideoConstraints(videoConstraints)
            }
        }
    }

    // // L'oggetto videoConstraints viene inferito correttamente da TypeScript
    // const videoConstraints = {
    //     deviceId: cameraDefault.deviceId,
    //     //width: selectedResolution ? selectedResolution.width : undefined,
    //     //height: selectedResolution ? selectedResolution.height : undefined,
    // }

    // CAMERA METHODS

    const aziendaSelected: AziendaObj = useAppSelector(selectAzienda)
    const tipiImageDocColliConfez: TipiImageDoc = useAppSelector(selectTipiImageDocColliConfez)
    const tipiImageDocColliConfezRighe: TipiImageDoc = useAppSelector(selectTipiImageDocColliConfezRighe)

    const dispatch = useAppDispatch()

    const blobToDataUrl = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    resolve(reader.result)
                } else {
                    reject(new Error('Formato immagine non valido'))
                }
            }
            reader.onerror = () => reject(new Error('Conversione immagine fallita'))
            reader.readAsDataURL(blob)
        })

    let COD_TIPO_IMAGE_DOC: string = ''
    let DES_TIPO_IMAGE_DOC: string | undefined
    switch (TIPO) {
        case TIPO_IMAGE.COLLI_CONFEZ:
            COD_TIPO_IMAGE_DOC = tipiImageDocColliConfez.COD_TIPO_IMAGE_DOC
            DES_TIPO_IMAGE_DOC = tipiImageDocColliConfez.DES_TIPO_IMAGE_DOC
            break
        case TIPO_IMAGE.COLLI_CONFEZ_RIGHE:
            COD_TIPO_IMAGE_DOC = tipiImageDocColliConfezRighe.COD_TIPO_IMAGE_DOC
            DES_TIPO_IMAGE_DOC = tipiImageDocColliConfezRighe.DES_TIPO_IMAGE_DOC
            break
    }

    const capture = React.useCallback(async () => {
        let IMMAGINE: string | null = null
        let currentCaptureMode: CaptureMode = null

        try {
            const track = webcamRef.current?.stream?.getVideoTracks?.()[0]
            if (track && typeof ImageCapture !== 'undefined') {
                const imageCapture = new ImageCapture(track)
                const blob: Blob = await imageCapture.takePhoto()
                IMMAGINE = await blobToDataUrl(blob)
                currentCaptureMode = 'image-capture'
            }
        } catch (error) {
            console.error('ImageCapture non disponibile, uso fallback con getScreenshot', error)
        }

        if (!IMMAGINE) {
            IMMAGINE = webcamRef.current?.getScreenshot() || null
            if (IMMAGINE) {
                currentCaptureMode = 'fallback'
            }
        }

        if (IMMAGINE == null) {
            alert("Errore durante la cattura dell'immagine. Riprova.")
            return
        }

        const AZIENDA_ID = aziendaSelected.AZIENDA_ID

        uploadImage(id, DOC_ID, TIPO, AZIENDA_ID, COD_TIPO_IMAGE_DOC, IMMAGINE, (image: ImmagineRequest) => {
            dispatch(addImage(image))
        })

        setCaptureMode(currentCaptureMode)
    }, [webcamRef, aziendaSelected.AZIENDA_ID, id, DOC_ID, TIPO, COD_TIPO_IMAGE_DOC, dispatch])

    async function camera(deviceId: string) {
        const videoPlayer = document.createElement('video')
        videoPlayer.style.width = '0px'
        videoPlayer.style.height = '0px'
        document.body.appendChild(videoPlayer)

        const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { deviceId } })
        videoPlayer.srcObject = stream
        videoPlayer.play()

        const track = stream.getVideoTracks()[0]

        // track.applyConstraints({
        //     advanced: [{ torch: true }],
        // })

        console.log('track', track)

        return () => {
            track.stop()
            document.body.removeChild(videoPlayer)
        }
    }

    useEffect(() => {
        let cleanup: (() => void) | undefined;

        if (cameraDefault.deviceId !== NO_CAMERA.deviceId) {
            camera(cameraDefault.deviceId).then((c) => {
                cleanup = c;
                console.log(cameraDefault.deviceId)
            });
        }

        return () => {
            if (cleanup) cleanup();
        };
    }, []);

    return (
        <div style={{ width: '100%', height: '88%', textAlign: 'center', marginTop: '3%' }}>
            {cameraCapabilities && (
                <div
                    style={{
                        padding: '15px',
                        background: 'rgba(255, 255, 255, 0.4)',
                        position: 'absolute',
                        top: '5%',
                        marginLeft: '0px',
                        textAlign: 'left',
                    }}
                >
                    <ul style={{ listStyleType: 'none', padding: 0, margin: 0, fontSize: '10px' }}>
                        <li>
                            <strong>Dispositivo:</strong>{' '}
                            {devices.find((d) => d.deviceId === cameraDefault.deviceId)?.label ||
                                cameraDefault.deviceId}
                        </li>
                        <li>
                            <strong>Risoluzione Massima Supportata:</strong> {cameraCapabilities.width?.max} x{' '}
                            {cameraCapabilities.height?.max}
                        </li>
                        {/* Controllo per assicurarsi che i valori esistano prima di fare calcoli */}
                        {cameraCapabilities.width?.max && cameraCapabilities.height?.max && (
                            <li>
                                <strong>Aspect Ratio Nativo:</strong>{' '}
                                {getAspectRatio(cameraCapabilities.width.max, cameraCapabilities.height.max)}
                            </li>
                        )}
                        <li>
                            <strong>Frame Rate Massimo:</strong> {cameraCapabilities.frameRate?.max} fps
                        </li>
                    </ul>
                </div>
            )}
            <Webcam
                style={{ width: '100%', height: '88%' }}
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                screenshotQuality={1}
                imageSmoothing={true}
                onUserMedia={onUserMedia}
            />
            <Button variant="contained" onClick={capture} style={{ position: 'relative', bottom: '12%', zIndex: 2 }}>
                Take photo!
            </Button>
            {captureMode && (
                <div
                    style={{
                        position: 'relative',
                        bottom: '10%',
                        zIndex: 2,
                        color: captureMode === 'image-capture' ? '#0a7a2f' : '#8a6d1d',
                        fontWeight: 600,
                        fontSize: '12px',
                    }}
                >
                    Metodo scatto: {captureMode === 'image-capture' ? 'alta qualità' : 'Fallback getScreenshot'}
                </div>
            )}
            {/* <Button
                variant="contained"
                onClick={capture}
                style={{ position: 'relative', bottom: '12%', left: '10%', zIndex: 2 }}
            >
                Flash
            </Button> */}
        </div>
    )
}
